/**
 * 1:1 call ring UI state, driven by the SDK's `CallsModule`.
 *
 * The SDK owns the ring HANDSHAKE (invite / incoming / accept / reject / cancel /
 * no-answer timeout, over the owner `user:<presenceId>` channel). This store is
 * the thin app-side glue: it holds what the UI renders (the incoming-call
 * overlay + the active call screen) and forwards user actions to the module.
 *
 * There is NO real media yet — "connected" here means the ring was answered, not
 * that audio/video is flowing. Media/room-join is separate (RTC) work.
 *
 * The module is attached once by `useOwnUserChannel` over the app's EXISTING
 * owner channel (we can't spin up the SDK's PresenceModule — it would fight that
 * hook for the same `(socket, topic)`), via a small OwnerChannelHost adapter.
 */

import { create } from 'zustand'
import type { CallsModule, CallSession, CallType } from '@/sdk/instavcMessenger'

export type CallMode = CallType // 'audio' | 'video'

/** The other party we're ringing / talking to. */
export interface CallPeer {
  id: string // contactPresenceId
  name: string
  imageUrl?: string
}

export interface ActiveCall extends CallPeer {
  callId: string
  mode: CallMode
  direction: 'outgoing' | 'incoming'
  /** `calling` = ringing out / just answered-in; `connected` = ring answered. */
  state: 'calling' | 'connected'
  /**
   * The conversation this call belongs to, when the caller named one.
   *
   * Already travelled as far as `invite`'s opaque payload and was dropped here,
   * so the call UI had no way to ask what KIND of conversation it was in — which
   * is why "Add to call" had to be decided by a prop nobody could set. Kept so
   * the screen can look the conversation up and answer that from real data.
   */
  conversationId?: string
}

export interface IncomingCallUi extends CallPeer {
  callId: string
  mode: CallMode
  payload: Record<string, unknown>
}

interface CallsState {
  incoming: IncomingCallUi | null
  active: ActiveCall | null
  /** The SDK module — set once the owner channel is live. */
  module: CallsModule | null

  attach: (module: CallsModule) => void
  detach: () => void

  /** Caller: ring `peer`'s connected devices. `payload` is opaque (e.g. conversationId). */
  startCall: (peer: CallPeer, mode: CallMode, payload?: Record<string, unknown>) => void
  /** Callee: answer the current incoming ring. */
  acceptIncoming: () => void
  /** Callee: decline the current incoming ring. */
  declineIncoming: () => void
  /** Either side: hang up / cancel the active call. */
  endActive: () => void

  /** Internal — SDK event sinks. */
  _onIncoming: (c: CallSession, peerName?: string, peerImage?: string) => void
  _onStateChange: (c: CallSession) => void
}

const TERMINAL = new Set(['rejected', 'cancelled', 'missed', 'ended'])

export const useCallsStore = create<CallsState>((set, get) => ({
  incoming: null,
  active: null,
  module: null,

  attach: (module) => set({ module }),
  detach: () => set({ module: null, incoming: null, active: null }),

  startCall: (peer, mode, payload = {}) => {
    const { module } = get()
    if (!module || get().active) return
    // Optimistic outgoing UI; the real callId lands via the `ringing` stateChange.
    set({
      active: {
        ...peer,
        callId: '',
        mode,
        direction: 'outgoing',
        state: 'calling',
        conversationId: typeof payload.conversationId === 'string' ? payload.conversationId : undefined,
      },
    })
    void module.invite(peer.id, { callType: mode, payload }).catch(() => set({ active: null }))
  },

  acceptIncoming: () => {
    const { module, incoming } = get()
    if (!module || !incoming) return
    void module.accept(incoming.callId)
    set({
      incoming: null,
      active: {
        id: incoming.id,
        name: incoming.name,
        imageUrl: incoming.imageUrl,
        callId: incoming.callId,
        mode: incoming.mode,
        direction: 'incoming',
        state: 'connected',
        // The caller put it in the invite payload; carry it across the answer
        // so an answered call knows its conversation just as an outgoing one does.
        conversationId:
          typeof incoming.payload?.conversationId === 'string'
            ? incoming.payload.conversationId
            : undefined,
      },
    })
  },

  declineIncoming: () => {
    const { module, incoming } = get()
    if (incoming) void module?.reject(incoming.callId)
    set({ incoming: null })
  },

  endActive: () => {
    const { module, active } = get()
    if (active) {
      // Unanswered outgoing ring → cancel; an answered call → end (notifies peer).
      if (active.state === 'calling' && active.direction === 'outgoing') {
        void module?.cancel(active.callId)
      } else if (active.state === 'connected') {
        void module?.end(active.callId)
      }
    }
    set({ active: null })
  },

  _onIncoming: (c, peerName, peerImage) => {
    // Don't ring over an in-progress call — auto-decline instead (busy).
    if (get().active || get().incoming) {
      void get().module?.reject(c.callId, 'busy')
      return
    }
    set({
      incoming: {
        id: c.peerId,
        name: peerName || c.callerName || 'Unknown',
        imageUrl: peerImage,
        callId: c.callId,
        mode: c.callType,
        payload: c.payload,
      },
    })
  },

  _onStateChange: (c) => {
    set((s) => {
      // Fill the real callId onto an optimistic outgoing ring.
      if (c.direction === 'outgoing' && c.state === 'ringing') {
        if (s.active && s.active.direction === 'outgoing' && !s.active.callId && s.active.id === c.peerId) {
          return { active: { ...s.active, callId: c.callId } }
        }
        return {}
      }
      // Answered → connected (caller side).
      if (c.state === 'accepted' && s.active && s.active.callId === c.callId) {
        return { active: { ...s.active, state: 'connected' } }
      }
      // Any terminal → drop whichever UI holds this call.
      if (TERMINAL.has(c.state)) {
        const next: Partial<CallsState> = {}
        if (s.active && s.active.callId === c.callId) next.active = null
        if (s.incoming && s.incoming.callId === c.callId) next.incoming = null
        return next
      }
      return {}
    })
  },
}))
